import Phaser from "phaser";
import { WORLD_TILE_PIXELS, type ItemRarity } from "odyssey-shared";
import { groundAlpha, type GroundLook } from "../../ground/find.ts";
import { GROUND_FLOAT_PX } from "../../ground/motion.ts";
import { GROUND_ITEM_TILES, type GroundReading } from "../../ground/view.ts";
import type { WorldUiModule } from "../hook.ts";
import { LINE, onScreen } from "../nameplates/layout.ts";
import { NameplatePool } from "../nameplates/pool.ts";
import { PIXEL_FONT_KEY, pixelFontSize } from "../pixel-font.ts";
import { LABEL_GAP, LabelStack, labelLeft, labelTop } from "./layout.ts";
import type { GroundLabelReading, GroundLabelReport } from "./report.ts";
import { rarityTint } from "./style.ts";
import { inLabelReach, labelTile, labelUnder } from "./visible.ts";

/**
 * Ground item labels on the canvas (P2.8-T76, the author's request of 2026-10-10, "Can we show the items name, in the
 * colour of their rarity?", and their choice "Nearby + hover"): over a ground item within `GROUND_LABEL_TILES` of the
 * local character's feet, over the item under the pointer, and over every item on screen while Show item names' key is
 * held, its item's name as the item card names it, from the page's rules, in the rarity colour the bag's slots and the
 * card use (`rarityColour`), one `BitmapText` in the pixel face at the nameplate's size with the face's own 1 px ink
 * outline, which keeps it legible on grass and on light floors as it keeps a nameplate. Another character's owned item
 * labels at the strength the item draws at (`groundAlpha`); the page's own and a public item's at full strength. The
 * UI camera draws them, so a label pixel is a HUD pixel at every world zoom, positioned on the device grid as a
 * nameplate is (`layout.ts`).
 *
 * A label stands above the item's float at its highest, fixed while the item bobs under it: text that moved with a
 * 2 pixel float would read as a wobble, and a fixed label is never laid out on a frame the float alone changes. Items
 * may overlap freely; a label that would cover another's text is nudged up or down just enough to clear it, as the
 * author asked ("Its how PoE and Diablo did it.", `LabelStack`). A label shows and hides at once, as a nameplate does, so
 * reduced motion has nothing to hold still here.
 *
 * Labels come from a pool keyed by handle (`nameplates/pool.ts`). Which labels show is worked out again only when the
 * local character enters another tile, the item under the pointer changes, the key's hold changes or the items drawn
 * change (a new list from the ground view); the stack only then or when the world zoom or the UI scale changes; a name
 * is set only when a label's item changes, a tint or strength only when its rarity or look does; and a label is moved
 * only when its anchor moves. A frame of steady play sets nothing and allocates nothing but the projection's
 * coordinates.
 */

/**
 * Half a ground item's drawn square in world pixels: the box the pointer hovers it within, as a click picks it up.
 */
const HALF = (WORLD_TILE_PIXELS * GROUND_ITEM_TILES) / 2;

/**
 * How far above an item's point its label's anchor stands, in world pixels: the icon's top at rest and the float's
 * height, so the label clears the item at the top of its float.
 */
const ABOVE_ITEM = HALF + GROUND_FLOAT_PX;

/**
 * One item's label and what it shows and where.
 */
interface Label {
  readonly text: Phaser.GameObjects.BitmapText;
  reading: GroundReading | null;
  name: string;
  rarity: ItemRarity | null;
  look: GroundLook | null;
  width: number;
  wanted: boolean;
  shift: number;
  left: number;
  top: number;
  scale: number;
  shown: boolean;
}

/**
 * Hides a label, if shown.
 *
 * @param label The label.
 */
function hide(label: Label): void {
  if (label.shown) {
    label.text.setVisible(false);
    label.shown = false;
  }
}

/**
 * The ground item labels surface.
 *
 * @param report The game report's `groundLabels` entry, whose `held` the scene writes.
 * @returns The surface's module, listed before the nameplates so a character's plate draws over an item's label.
 */
export function groundLabels(report: GroundLabelReport): WorldUiModule {
  return {
    id: "ground-labels",
    /**
     * Builds the pool and the stack, and lists the labels for the report.
     *
     * @param context The UI layer, the projection, the local character and the ground items the scene draws.
     * @returns The surface.
     */
    create(context) {
      const scene = context.scene;
      const labels = new NameplatePool<Label>(() => {
        const text = context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PIXEL_FONT_KEY, "", pixelFontSize("nameplate")));
        text.setOrigin(0, 0).setVisible(false);
        return { text, reading: null, name: "", rarity: null, look: null, width: 0, wanted: false, shift: 0, left: Number.NaN, top: Number.NaN, scale: 0, shown: false };
      });
      const stack = new LabelStack();
      const local = { x: 0, y: 0 };
      const anchor = { x: 0, y: 0 };
      const at = new Phaser.Math.Vector2();
      const camera = context.worldCamera;
      let frame = 0;
      let lastReadings: readonly GroundReading[] | null = null;
      let lastTileX = Number.NaN;
      let lastTileY = Number.NaN;
      let pointerX = Number.NEGATIVE_INFINITY;
      let pointerY = Number.NEGATIVE_INFINITY;
      let hovered = -1;
      let lastHovered = -1;
      let lastHeld = false;
      let lastZoom = 0;
      let lastScale = 0;

      /**
       * Returns a label its item has left: hidden and holding no reading. It keeps its text, tint and strength, which
       * the next item that takes it sets again only where they differ.
       *
       * @param label The label.
       */
      const release = (label: Label): void => {
        hide(label);
        label.reading = null;
        label.wanted = false;
        label.left = Number.NaN;
      };

      /**
       * Sets a label's name, tint and strength for its item, each only where it differs.
       *
       * @param label The label.
       * @param reading The item.
       */
      const restyle = (label: Label, reading: GroundReading): void => {
        if (label.name !== reading.name) {
          label.text.setText(reading.name);
          label.width = label.text.width;
          label.name = reading.name;
          label.left = Number.NaN;
          report.texts += 1;
        }
        if (label.rarity !== reading.rarity) {
          label.text.setTint(rarityTint(reading.rarity));
          label.rarity = reading.rarity;
          report.looks += 1;
        }
        if (label.look !== reading.look) {
          label.text.setAlpha(groundAlpha(reading.look));
          label.look = reading.look;
          report.looks += 1;
        }
        label.reading = reading;
      };

      /**
       * Writes an item's label anchor this frame, in device pixels.
       *
       * @param reading The item.
       * @param scale The UI scale.
       */
      const project = (reading: GroundReading, scale: number): void => {
        context.project(reading.x, reading.y - ABOVE_ITEM, anchor);
        anchor.x = Math.round(anchor.x * scale);
        anchor.y = Math.round(anchor.y * scale);
      };

      /**
       * The item under the pointer, looked up again only when the world point under it moves to another whole world
       * pixel or the items drawn change; -1 while the pointer is off the canvas.
       *
       * @param readings The items drawn.
       * @returns Its handle, or -1.
       */
      const pointed = (readings: readonly GroundReading[]): number => {
        let x = Number.NEGATIVE_INFINITY;
        let y = Number.NEGATIVE_INFINITY;
        if (scene.input.isOver) {
          const pointer = scene.input.activePointer;
          camera.getWorldPoint(pointer.x, pointer.y, at);
          x = Math.floor(at.x);
          y = Math.floor(at.y);
        }
        if (x !== pointerX || y !== pointerY || readings !== lastReadings) {
          pointerX = x;
          pointerY = y;
          hovered = x === Number.NEGATIVE_INFINITY ? -1 : labelUnder(readings, at.x, at.y, HALF);
        }
        return hovered;
      };

      /**
       * Places the labels that show, from their anchors this frame, nudging any that would cover another.
       *
       * @param readings The items drawn.
       * @param scale The UI scale.
       */
      const restack = (readings: readonly GroundReading[], scale: number): void => {
        stack.reset();
        for (let index = 0; index < readings.length; index += 1) {
          const reading = readings[index] as GroundReading;
          const label = labels.get(reading.handle) as Label;
          if (label.wanted) {
            project(reading, scale);
            stack.add(reading.handle, labelLeft(anchor.x, scale, label.width), labelTop(anchor.y, scale), label.width * scale);
          }
        }
        stack.solve(LINE.height * scale, LABEL_GAP * scale);
        let placed = 0;
        for (let index = 0; index < readings.length; index += 1) {
          const label = labels.get((readings[index] as GroundReading).handle) as Label;
          label.shift = label.wanted ? stack.shift(placed++) : 0;
        }
        report.stacks += 1;
      };

      /**
       * Reads every label held, for the report: made on request, never on a frame.
       *
       * @returns One reading a label.
       */
      report.list = (): readonly GroundLabelReading[] => {
        const readings: GroundLabelReading[] = [];
        labels.forEachActive((label, handle) => {
          readings.push({ handle, text: label.text.text, shown: label.shown, tint: label.text.tintTopLeft, alpha: label.text.alpha, x: label.text.x, y: label.text.y, width: label.width, shift: label.shift, inUiLayer: label.text.displayList === context.layer });
        });
        return readings;
      };

      return {
        /**
         * Takes a label for every item drawn, works out which show and their stack when something they depend on
         * changed, places the shown ones over their items, then returns the labels of items that left.
         */
        update() {
          frame += 1;
          let shown = 0;
          const readings = context.ground.readings();
          if (context.characters.local(local)) {
            const scale = context.scale.current;
            const zoom = camera.zoom;
            const tileX = labelTile(local.x);
            const tileY = labelTile(local.y);
            const under = pointed(readings);
            const held = report.held;
            const check = readings !== lastReadings || tileX !== lastTileX || tileY !== lastTileY || under !== lastHovered || held !== lastHeld;
            for (let index = 0; index < readings.length; index += 1) {
              const reading = readings[index] as GroundReading;
              const label = labels.take(reading.handle, frame);
              if (label.reading !== reading) {
                restyle(label, reading);
              }
              if (check) {
                label.wanted = held || under === reading.handle || inLabelReach(reading.x - local.x, reading.y - local.y);
              }
            }
            if (check) {
              lastReadings = readings;
              lastTileX = tileX;
              lastTileY = tileY;
              lastHovered = under;
              lastHeld = held;
              report.checks += 1;
            }
            if (check || zoom !== lastZoom || scale !== lastScale) {
              restack(readings, scale);
              lastZoom = zoom;
              lastScale = scale;
            }
            const height = LINE.height * scale;
            for (let index = 0; index < readings.length; index += 1) {
              const reading = readings[index] as GroundReading;
              const label = labels.get(reading.handle) as Label;
              if (!label.wanted) {
                hide(label);
                continue;
              }
              project(reading, scale);
              const left = labelLeft(anchor.x, scale, label.width);
              const top = labelTop(anchor.y, scale) + label.shift;
              if (!onScreen(left, top, label.width * scale, height, camera.width, camera.height)) {
                hide(label);
                continue;
              }
              if (left !== label.left || top !== label.top || scale !== label.scale) {
                label.text.setPosition(left / scale, top / scale);
                label.left = left;
                label.top = top;
                label.scale = scale;
                report.layouts += 1;
              }
              if (!label.shown) {
                label.text.setVisible(true);
                label.shown = true;
              }
              shown += 1;
            }
          } else {
            lastReadings = null;
          }
          labels.sweep(frame, release);
          report.active = labels.active;
          report.free = labels.free;
          report.created = labels.created;
          report.shown = shown;
        },
        /**
         * Forgets the report's list, which would hold the destroyed labels.
         */
        destroy() {
          report.list = () => [];
        },
      };
    },
  };
}
